import { useEffect, useState, useCallback } from "react"
import debounce from "lodash.debounce"
import { Maybe, Slice } from "@flockcover/flock-svc-core-types"
import { ApiError, Combobox } from "@shared/primitive-components"
import { QueryResult } from "@shared/types"

type OnSelectedValue = (selectedValue: Maybe<string>) => void

// Updated SearchQueryFn type
export type SearchQueryFn = (searchTerm: Maybe<string>) => Promise<Slice<{ [key: string]: string }>>

export type APISearchProps = {
  onSelectedValue: OnSelectedValue
  queryFn: SearchQueryFn
  matchKey: string
  dataTestId?: string
  disabled?: boolean
}

export const APISearch = ({ onSelectedValue, queryFn, matchKey, dataTestId, disabled }: APISearchProps) => {
  const [search, setSearch] = useState<Maybe<string>>(null)
  const [queryResult, setQueryResult] = useState<QueryResult<Slice<{ [key: string]: string }>>>({ state: `loading` })

  const fetchData = useCallback(
    async (searchTerm: Maybe<string>) => {
      setQueryResult({ state: `loading` })
      try {
        const data = await queryFn(searchTerm)
        setQueryResult({ state: `success`, data })
      } catch (error) {
        setQueryResult({ state: `failure`, error })
      }
    },
    [queryFn]
  )

  useEffect(() => {
    fetchData(search)

    if (search === ``) {
      onSelectedValue(null)
    }
  }, [search, fetchData, onSelectedValue])

  const mapValues = () => {
    if (queryResult.state === `success`) {
      const values = queryResult.data.items
        .map((item) => item[matchKey])
        .filter((item): item is string => item !== null)
      return values.length > 0 ? values : [`No results found`]
    }
    return [`Loading ...`]
  }

  const debouncedSearch = debounce((value: string) => setSearch(value), 800)

  if (queryResult.state === `failure`) return <ApiError />

  return (
    <Combobox.Root items={mapValues()}>
      <Combobox.Input
        disabled={disabled}
        data-testid={dataTestId}
        placeholder="Search here"
        onInputValueChange={debouncedSearch}
      />
      <Combobox.Content onValueSelect={(selectedValue: Maybe<string>) => onSelectedValue(selectedValue ?? null)} />
    </Combobox.Root>
  )
}

APISearch.defaultProps = {
  dataTestId: undefined,
  disabled: false,
}
